
import { USIEngineSettings } from "@/settings/usi";
import { ref, onMounted, defineComponent, Ref, computed } from "vue";
import { loadGameSetting, loadUSIEngineSetting } from "@/ipc/renderer";
import { Action, Mutation, useStore } from "@/store";
import {
  defaultGameSetting,
  GameSetting,
  PlayerSetting,
  validateGameSetting,
} from "@/settings/game";
import { showModalDialog } from "@/helpers/dialog";
import * as uri from "@/uri";
import ButtonIcon from "@/components/primitive/ButtonIcon.vue";
import { readInputAsNumber } from "@/helpers/form";

export default defineComponent({
  name: "GameDialog",
  components: {
    ButtonIcon,
  },
  setup() {
    const store = useStore();
    const dialog: Ref = ref(null);
    const blackPlayerSelect: Ref = ref(null);
    const whitePlayerSelect: Ref = ref(null);
    const hours: Ref = ref(null);
    const minutes: Ref = ref(null);
    const byoyomi: Ref = ref(null);
    const increment: Ref = ref(null);
    const startPosition: Ref = ref(null);
    const enableEngineTimeout: Ref = ref(null);
    const humanIsFront: Ref = ref(null);
    const gameSetting = ref(defaultGameSetting());
    const engineSetting = ref(new USIEngineSettings());

    store.commit(Mutation.RETAIN_BUSSY_STATE);

    onMounted(async () => {
      showModalDialog(dialog.value);
      try {
        gameSetting.value = await loadGameSetting();
        engineSetting.value = await loadUSIEngineSetting();
        store.commit(Mutation.RELEASE_BUSSY_STATE);
      } catch (e) {
        store.commit(Mutation.PUSH_ERROR, e);
        store.commit(Mutation.CLOSE_DIALOG);
      }
    });

    const buildPlayerSetting = (playerURI: string): PlayerSetting => {
      if (uri.isUSIEngine(playerURI)) {
        const engine = engineSetting.value.getEngine(playerURI);
        return {
          name: engine.name,
          uri: playerURI,
          usi: engine,
        };
      }
      return {
        name: "人",
        uri: uri.ES_HUMAN,
      };
    };

    const onStart = () => {
      const gameSetting: GameSetting = {
        black: buildPlayerSetting(blackPlayerSelect.value.value),
        white: buildPlayerSetting(whitePlayerSelect.value.value),
        timeLimit: {
          timeSeconds:
            (readInputAsNumber(hours.value) * 60 +
              readInputAsNumber(minutes.value)) *
            60,
          byoyomi: readInputAsNumber(byoyomi.value),
          increment: readInputAsNumber(increment.value),
        },
        startPosition:
          startPosition.value.value !== "current"
            ? startPosition.value.value
            : undefined,
        enableEngineTimeout: enableEngineTimeout.value.checked,
        humanIsFront: humanIsFront.value.checked,
      };
      const error = validateGameSetting(gameSetting);
      if (error) {
        store.commit(Mutation.PUSH_ERROR, error);
      } else {
        store.dispatch(Action.START_GAME, gameSetting);
      }
    };

    const onCancel = () => {
      store.commit(Mutation.CLOSE_DIALOG);
    };

    const onSwapColor = () => {
      [blackPlayerSelect.value.value, whitePlayerSelect.value.value] = [
        whitePlayerSelect.value.value,
        blackPlayerSelect.value.value,
      ];
    };

    const defaultValues = computed(() => {
      return {
        black: { type: gameSetting.value.black.uri },
        white: { type: gameSetting.value.white.uri },
        timeLimit: {
          hours: Math.floor(gameSetting.value.timeLimit.timeSeconds / 3600),
          minutes:
            Math.floor(gameSetting.value.timeLimit.timeSeconds / 60) % 60,
          byoyomi: gameSetting.value.timeLimit.byoyomi,
          increment: gameSetting.value.timeLimit.increment,
        },
        startPosition:
          gameSetting.value.startPosition !== undefined
            ? gameSetting.value.startPosition
            : "current",
        enableEngineTimeout: gameSetting.value.enableEngineTimeout,
        humanIsFront: gameSetting.value.humanIsFront,
      };
    });

    const players = computed(() => {
      return [
        { name: "人", uri: uri.ES_HUMAN },
        ...engineSetting.value.engineList,
      ];
    });

    return {
      dialog,
      blackPlayerSelect,
      whitePlayerSelect,
      hours,
      minutes,
      byoyomi,
      increment,
      startPosition,
      enableEngineTimeout,
      humanIsFront,
      defaultValues,
      players,
      onStart,
      onCancel,
      onSwapColor,
    };
  },
});
